import React from "react";
import AhaSessionModulesField from "../../components/aha/AhaSessionModulesField";
import { fetchClassCertificateTypes, getAhaCatalog, getAhaEligibility } from "../../api/apiClient";
import { useTheme, alpha } from "@mui/material/styles";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, TextField, CircularProgress, Menu, MenuItem, Divider, Snackbar, Alert } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  createSession,
  updateSession,
  deleteSession,
  fetchSessionOpenings,
} from "../../api/backend";
import RoomPicker from "../../components/RoomPicker";
import { parseLocalDate } from "../../utils/dateUtils";
import { fetchRooms, fetchSites } from "../../api/rooms";
import LocationSelect from "../../components/LocationSelect";
import { VIRTUAL, isVirtual, keyForSession, legacyKey, locationText, sessionLocationFields } from "../../utils/locations";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";


const D = {
  primary50:  "color-mix(in srgb, var(--color-primary) 8%, transparent)",
  ink:   "var(--color-ink)",
  ink2:  "rgba(31,22,51,0.75)",
  ink3:  "rgba(31,22,51,0.6)",
  ink4:  "rgba(31,22,51,0.5)",
  line:   "var(--color-hairline-cloud)",
  line2:  "var(--color-hairline-cloud)",
  surface:   "var(--color-surface-canvas-light)",
  surface2:  "var(--color-surface-press-light)",
  surface3:  "var(--color-surface-press-stronger)",
  green:     "var(--color-success)",
  green50:   "var(--color-success-bg)",
  green100:  "var(--color-success-bg)",
  amber:     "var(--color-warn)",
  amber50:   "var(--color-warn-bg)",
  live:      "#c23d68",
  live50:    "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)",
  live100:   "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger:    "var(--color-danger)",
  danger50:  "var(--color-danger-bg)",
  danger100: "var(--color-danger-bg)",
  shadowSm:  "var(--shadow-card-sm)",
  primary: "var(--color-primary)",
  primary10: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
  primary700: "color-mix(in srgb, var(--color-primary) 85%, black)",
};

function useD() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return {
    ...D,
    primary,
    primary700: theme.palette.primary.dark,
    primary10: alpha(primary, 0.06),
  };
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function parseDateInfo(s) {
  if (!s) return null;
  const d = parseLocalDate(s);
  if (!d || isNaN(d)) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const dd = new Date(d); dd.setHours(0,0,0,0);
  return { month: MONTHS[d.getMonth()], day: d.getDate(), isPast: dd < today, isToday: dd.getTime() === today.getTime() };
}

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,"0")} ${h >= 12 ? "PM" : "AM"}`;
}

function getStatus(s) {
  if (s.enabled) return "live";
  const dateStr = s.sessionDate || s.date;
  const d = dateStr ? parseLocalDate(dateStr) : null;
  if (d && !isNaN(d)) {
    const today = new Date(); today.setHours(0,0,0,0);
    d.setHours(0,0,0,0);
    return d < today ? "completed" : "upcoming";
  }
  // No usable date: attendance data is the only signal that it already happened
  if ((s.checkedIn ?? 0) > 0) return "completed";
  return "upcoming";
}

function makeStatus(D) {
  return {
    live:      { label: "Live",        bg: D.live50,  color: D.live,    dot: D.live },
    upcoming:  { label: "Scheduled",   bg: D.primary10, color: D.primary, dot: D.primary },
    completed: { label: "Completed",   bg: D.green50, color: D.green,   dot: D.green },
  };
}

function SvgIcon({ size = 14, sw = 2, children }) {
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={sw}
      strokeLinecap="round" strokeLinejoin="round">
      {children}
    </Box>
  );
}

const FlLabel = ({ children, optional }) => (
  <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>
    {children}{optional && <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D.ink4, ml: "4px" }}>— optional</Box>}
  </Box>
);

function makeControlSx(D) {
  return {
    "& .MuiOutlinedInput-root": {
      height: 44, borderRadius: "12px", bgcolor: D.surface2,
      "& fieldset": { borderColor: D.line, borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: D.ink3 },
      "&.Mui-focused fieldset": { borderColor: D.primary, borderWidth: "1.5px", boxShadow: `0 0 0 3px ${D.primary10}` },
      "&.Mui-focused": { bgcolor: "var(--color-surface-canvas-light)" },
      // Multiline must not inherit fixed single-line height (crushes placeholder/value lines).
      "&.MuiInputBase-multiline": {
        height: "auto",
        minHeight: 88,
        alignItems: "flex-start",
        py: "10px",
      },
    },
    "& .MuiInputBase-input": { fontSize: "14px", fontWeight: 500, color: D.ink, lineHeight: 1.45 },
  };
}

/** A standard course's session content: set by Training Center, not by the center that schedules the class. */
const STANDARD_SESSION_CONTENT = ["name", "topics", "sessionNumber", "sessionType", "ahaModuleKeys"];

const EMPTY = { name: "", date: "", startTime: "09:00", endTime: "17:00", locKey: "", roomId: null, topics: "" };

// ── Panel card shell ─────────────────────────────────────────────────────────
function PanelCard({ iconBg, iconColor, icon, title, count, headerRight, children }) {
  const D = useD();
  return (
    <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${D.line}`, borderRadius: "16px",
      boxShadow: D.shadowSm, overflow: "hidden", mb: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: iconBg || D.primary10,
            color: iconColor || D.primary, display: "grid", placeItems: "center" }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>
            {title}
          </Typography>
          {count != null && (
            <Box sx={{ fontSize: "11px", fontWeight: 700, px: "7px", py: "2px", borderRadius: "6px",
              bgcolor: D.surface3, color: D.ink3 }}>
              {count}
            </Box>
          )}
        </Box>
        {headerRight}
      </Box>
      <Box sx={{ pb: "8px" }}>{children}</Box>
    </Box>
  );
}

// ── Session row ──────────────────────────────────────────────────────────────
function SessionRow({
  session, capacity, onEdit, onDelete, onViewAttendance, canEdit, isLast,
  instructorMap, room, rooms = [], onChangeRoom, contentLocked = false,
}) {
  const D = useD();
  const STATUS = makeStatus(D);
  const dateInfo = parseDateInfo(session.sessionDate || session.date);
  const status = getStatus(session);
  const st = STATUS[status];
  const checkedIn = session.checkedIn ?? 0;
  const total = session.totalStudents ?? capacity ?? 0;
  const fillPct = total > 0 ? Math.min((checkedIn / total) * 100, 100) : 0;
  const barColor = status === "completed" ? D.green : status === "live" ? D.live : D.primary;
  const countColor = status === "completed" ? D.green : status === "live" ? D.live : D.ink;

  const [roomAnchorEl, setRoomAnchorEl] = React.useState(null);

  // Openings are shown here read-only; scheduling, assignment and sign-up approval live on the Instructors tab.
  const [openings, setOpenings] = React.useState([]);

  const loadOpenings = React.useCallback(() => {
    if (!session.id) return;
    fetchSessionOpenings(session.id).then(setOpenings).catch(() => {});
  }, [session.id]);

  React.useEffect(() => {
    loadOpenings();
  }, [loadOpenings]);

  const roomLabel = room?.name || "No room";
  const hasRoom = Boolean(room);

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "14px", px: "20px", py: "14px",
      borderBottom: isLast ? 0 : `1px solid ${D.line2}`,
      transition: "background .12s", "&:hover": { bgcolor: D.surface2 },
    }}>
      {/* Date column */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 44, flexShrink: 0 }}>
        {dateInfo ? (
          <>
            <Box sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: ".08em",
              textTransform: "uppercase", color: D.ink3 }}>
              {dateInfo.month}
            </Box>
            <Box sx={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-.04em",
              color: dateInfo.isPast ? D.ink3 : D.primary, lineHeight: 1 }}>
              {dateInfo.day}
            </Box>
          </>
        ) : (
          <Box sx={{ fontSize: "12px", color: D.ink4 }}>—</Box>
        )}
      </Box>

      {/* Divider */}
      <Box sx={{ width: "1px", height: 36, bgcolor: D.line2, flexShrink: 0 }} />

      {/* Body */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: "14px", fontWeight: 700, color: dateInfo?.isPast ? D.ink2 : D.ink,
          mb: "2px" }}>
          {session.name || session.title || `Session ${session.sessionNumber || ""}`}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap",
          fontSize: "12.5px", color: D.ink3 }}>
          {(session.startTime || session.endTime) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <SvgIcon size={12}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></SvgIcon>
              {fmtTime(session.startTime)}{session.endTime ? ` – ${fmtTime(session.endTime)}` : ""}
            </Box>
          )}
          {session.location && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <SvgIcon size={12}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/></SvgIcon>
              {session.location}
            </Box>
          )}

          {/* Room chip — quick assign (mirrors instructor) */}
          {(hasRoom || canEdit) && (
            <Box
              onClick={canEdit ? (e) => { e.stopPropagation(); setRoomAnchorEl(e.currentTarget); } : undefined}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                cursor: canEdit ? "pointer" : "default",
                px: "6px", py: "2px", borderRadius: "6px",
                bgcolor: hasRoom ? "transparent" : D.surface3,
                border: hasRoom ? "none" : `1px dashed ${D.line}`,
                transition: "all .12s",
                "&:hover": canEdit ? { bgcolor: D.primary10 } : {},
              }}
            >
              <MeetingRoomIcon sx={{ fontSize: 12, color: hasRoom ? D.ink3 : D.ink4 }} />
              <Box component="span" sx={{
                color: hasRoom ? D.ink3 : D.ink4,
                fontWeight: hasRoom ? 400 : 600,
                fontStyle: hasRoom ? "normal" : "italic",
              }}>
                {roomLabel}
              </Box>
              {canEdit && <KeyboardArrowDownIcon sx={{ fontSize: 13, color: D.ink4, ml: "-2px" }} />}
            </Box>
          )}

          {/* Openings */}
          {openings.map((o) => {
            const filledBy = instructorMap?.[o.assignedInstructorId];
            const filledByName = filledBy
              ? (filledBy.fullName || filledBy.name || filledBy.username || "").trim()
              : o.assignedInstructorId ? "Unknown" : null;
            return (
            <Box key={o.id} sx={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              px: "6px", py: "2px", borderRadius: "6px",
              bgcolor: o.status === "OPEN" ? D.amber50 : D.green50,
              fontSize: "11px", fontWeight: 700,
              color: o.status === "OPEN" ? D.amber : D.green,
            }}>
              {o.teachingRole} · {o.status === "OPEN" ? "OPEN" : filledByName || "FILLED"}
              {o.signupMode === "APPROVAL" && o.pendingSignupCount > 0 && ` · ${o.pendingSignupCount} pending`}
            </Box>
            );
          })}
        </Box>
      </Box>

      {/* Enrollment mini-bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", width: 110, flexShrink: 0 }}>
        <Box sx={{ flex: 1, height: 5, bgcolor: D.line2, borderRadius: "999px", overflow: "hidden" }}>
          <Box sx={{ height: "100%", width: `${fillPct}%`, bgcolor: barColor, borderRadius: "999px" }} />
        </Box>
        <Box sx={{ fontSize: "12px", fontWeight: 700, color: countColor, whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums" }}>
          {checkedIn}/{total}
        </Box>
      </Box>

      {/* Status pill */}
      <Box component="span" sx={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
        px: "9px", py: "3px", borderRadius: "999px", whiteSpace: "nowrap",
        bgcolor: st.bg, color: st.color,
      }}>
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: st.dot, flexShrink: 0 }} />
        {st.label}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
        {status === "completed" && (
          <Box component="button" onClick={() => onViewAttendance(session)} sx={{
            height: 32, px: "13px", borderRadius: "9px", border: `1.5px solid ${D.line}`,
            bgcolor: D.surface2, fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700,
            color: D.ink2, cursor: "pointer", whiteSpace: "nowrap",
            "&:hover": { bgcolor: D.surface3, color: D.ink },
          }}>
            View Attendance
          </Box>
        )}
        {canEdit && (
          <>
            <IconBtn label="Edit session" onClick={() => onEdit(session)}>
              <SvgIcon size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></SvgIcon>
            </IconBtn>
            {!contentLocked && (
              <IconBtn label="Delete session" danger onClick={() => onDelete(session.id)}>
                <SvgIcon size={13}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></SvgIcon>
              </IconBtn>
            )}
          </>
        )}
      </Box>

      {/* Room picker menu */}
      {canEdit && (
        <Menu
          anchorEl={roomAnchorEl}
          open={Boolean(roomAnchorEl)}
          onClose={() => setRoomAnchorEl(null)}
          PaperProps={{
            sx: { borderRadius: "12px", boxShadow: "var(--shadow-card-md)", minWidth: 240, maxHeight: 360 },
          }}
        >
          <Box sx={{ px: "12px", pt: "8px", pb: "4px" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: ".06em", color: D.ink4 }}>
              Assign Room
            </Typography>
          </Box>
          <MenuItem
            dense
            selected={!session.roomId}
            onClick={() => { onChangeRoom?.(session, null); setRoomAnchorEl(null); }}
            sx={{ fontSize: 13, gap: "8px" }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: D.ink3, fontStyle: "italic" }}>No room</Box>
          </MenuItem>
          {rooms.length > 0 && <Divider sx={{ my: "4px" }} />}
          {rooms.length === 0 && (
            <MenuItem dense disabled sx={{ fontSize: 12.5, color: D.ink4 }}>
              No rooms in Room Management yet
            </MenuItem>
          )}
          {rooms.map((r) => (
            <MenuItem
              key={r.id}
              dense
              selected={session.roomId === r.id}
              onClick={() => { onChangeRoom?.(session, r.id); setRoomAnchorEl(null); }}
              sx={{ fontSize: 13, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px", py: "8px" }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: D.ink }}>{r.name}</Box>
              <Box component="span" sx={{ fontSize: 11, color: D.ink3 }}>
                {[r.number ? `#${r.number}` : null, r.type, r.capacity != null ? `${r.capacity} cap` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      )}

    </Box>
  );
}

function IconBtn({ children, danger, onClick, label }) {
  return (
    <Box component="button" type="button" aria-label={label} title={label} onClick={onClick} sx={{
      width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`,
      bgcolor: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", cursor: "pointer",
      color: D.ink3, transition: "all .12s",
      "&:hover": danger
        ? { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 }
        : { bgcolor: D.surface3, color: D.ink },
    }}>
      {children}
    </Box>
  );
}

// ── Inline session form ──────────────────────────────────────────────────────
function SessionForm({ initial, onSave, onCancel, saving, title, sites = [], primaryKey = "", capacity, aha = null, contentLocked = false }) {
  const D = useD();
  const controlSx = makeControlSx(D);
  const [form, setForm] = React.useState(initial || EMPTY);
  React.useEffect(() => {
    setForm(initial || EMPTY);
  }, [initial]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  // Default to the class's primary location; a 100% virtual class only has virtual sessions.
  const virtualClass = isVirtual(primaryKey);
  const locKey = virtualClass ? VIRTUAL : (form.locKey || primaryKey);
  const legacyText = form.locKey?.startsWith("legacy:") ? locationText(form.locKey) : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px",
      bgcolor: "var(--color-surface-canvas-light)", border: `1.5px dashed ${D.line}`,
      borderRadius: "14px", p: "18px", mx: "20px", mb: "8px", mt: "8px" }}>
      <Box sx={{ fontSize: "13.5px", fontWeight: 700, color: D.ink, display: "flex", alignItems: "center", gap: "8px", fontFamily: "var(--font-display)" }}>
        <SvgIcon size={15}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></SvgIcon>
        {title}
      </Box>
      <Box>
        <FlLabel optional>Session Name</FlLabel>
        <TextField fullWidth size="small" placeholder="e.g. Airway Management" value={form.name || ""} onChange={set("name")} sx={controlSx} disabled={contentLocked} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box>
          <FlLabel>Date</FlLabel>
          <TextField fullWidth size="small" type="date" value={form.date || ""} onChange={set("date")} sx={controlSx} InputLabelProps={{ shrink: true }} />
        </Box>
        <Box>
          <FlLabel>Start Time</FlLabel>
          <TextField fullWidth size="small" type="time" value={form.startTime || "09:00"} onChange={set("startTime")} sx={controlSx} />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box>
          <FlLabel>End Time</FlLabel>
          <TextField fullWidth size="small" type="time" value={form.endTime || "17:00"} onChange={set("endTime")} sx={controlSx} />
        </Box>
        <Box>
          <FlLabel>Location</FlLabel>
          <LocationSelect aria-label="Session location" value={locKey} sites={sites} virtualOnly={virtualClass} legacyText={legacyText}
            onChange={(v) => setForm((f) => ({ ...f, locKey: v, roomId: null }))}
            sx={{ width: "100%", height: 40, px: "12px", borderRadius: "10px", border: `1.5px solid ${D.line}`, font: "inherit", fontSize: "14px", bgcolor: "transparent", color: D.ink }} />
        </Box>
      </Box>
      {!isVirtual(locKey) && <Box>
        <FlLabel optional>Room</FlLabel>
        <RoomPicker
          minCapacity={capacity || undefined}
          siteId={/^\d+$/.test(locKey) ? locKey : undefined}
          value={form.roomId ?? null}
          onChange={(id) => setForm((f) => ({ ...f, roomId: id }))}
          date={form.date || null}
          startTime={form.startTime || null}
          endTime={form.endTime || null}
          excludeSessionId={form.id ?? null}
        />
      </Box>}
      <Box>
        <FlLabel optional>Topics covered</FlLabel>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          placeholder="e.g. Forcible entry tool selection"
          value={form.topics || ""}
          onChange={set("topics")}
          sx={controlSx}
          disabled={contentLocked}
        />
        <Box sx={{ fontSize: "11.5px", color: D.ink4, mt: "6px", lineHeight: 1.4 }}>
          One topic per line. Shown to students and instructors when they expand this session.
        </Box>
      </Box>
      {!contentLocked && aha?.modules?.length > 0 && (
        <AhaSessionModulesField modules={aha.modules} value={form.ahaModuleKeys || []} locked={aha.locked}
          onChange={(keys) => setForm((f) => {
            // The session is named after the modules it teaches; a name the user typed themselves is left alone.
            const nameOf = (ks) => aha.modules.filter((m) => ks.includes(m.key)).map((m) => m.name).join(", ");
            const auto = !f.name || f.name === nameOf(f.ahaModuleKeys || []);
            return { ...f, ahaModuleKeys: keys, ...(auto ? { name: nameOf(keys) } : {}) };
          })}
          labelSx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: D.ink3, mb: "6px" }} />
      )}
      {!contentLocked && aha?.needsProduct && (
        <Box data-testid="aha-session-needs-product" sx={{ fontSize: "12.5px", color: D.ink3, lineHeight: 1.45 }}>
          This class issues AHA eCards. Choose its AHA course on the Details tab, then pick the AHA modules taught in this session here.
        </Box>
      )}
      <Box sx={{ display: "flex", gap: "9px", justifyContent: "flex-end" }}>
        <Box component="button" onClick={onCancel} sx={{
          height: 32, px: "13px", borderRadius: "9px", border: `1.5px solid ${D.line}`,
          bgcolor: "transparent", fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700,
          color: D.ink3, cursor: "pointer", "&:hover": { bgcolor: D.surface3, color: D.ink },
        }}>Cancel</Box>
        <Box component="button" onClick={() => {
          const { ahaModuleKeys, ...rest } = form;
          onSave({ ...rest, locKey, ...(!contentLocked && aha?.modules?.length > 0 && !aha.locked ? { ahaModuleKeys: ahaModuleKeys || [] } : {}) });
        }} disabled={!form.date || !locKey || saving} sx={{
          height: 32, px: "13px", borderRadius: "9px", border: 0,
          bgcolor: !form.date || !locKey ? D.surface3 : D.primary,
          color: !form.date || !locKey ? D.ink3 : "var(--color-on-primary)",
          fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700,
          cursor: !form.date ? "default" : "pointer",
          display: "inline-flex", alignItems: "center", gap: "6px",
          boxShadow: !form.date ? "none" : `0 4px 10px -4px ${alpha(D.primary, 0.4)}`,
          "&:hover:not(:disabled)": { bgcolor: D.primary700 },
        }}>
          {saving ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : (
            <SvgIcon size={13} sw={2.5}><polyline points="20 6 9 17 4 12"/></SvgIcon>
          )}
          {saving ? "Saving…" : "Schedule Session"}
        </Box>
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CoordinatorSessionsTab({
  sessions = [],
  setSessions,
  refreshCourse,
  canEdit,
  course,
  courseInstructors = [],
  enrolledCount = 0,
  contentLocked = false,
}) {
  const D = useD();
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [showAdd, setShowAdd] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?edit=<sessionId> (e.g. from the Details tab's AHA setup checklist) opens that session's edit form once.
  const editParam = searchParams.get("edit");
  React.useEffect(() => {
    if (!editParam || !canEdit) return;
    const target = sessions.find((s) => String(s.id) === editParam);
    if (!target) return;
    setEditTarget(target);
    setShowAdd(false);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [editParam, sessions, canEdit, searchParams, setSearchParams]); // runs when the target or list arrives
  const [saving, setSaving] = React.useState(false);
  const [instructorError, setInstructorError] = React.useState(null);
  const [rooms, setRooms] = React.useState([]);
  const [sites, setSites] = React.useState([]);
  // AHA classes: the modules of the class's product, and whether card requests already fix them.
  const [aha, setAha] = React.useState(null);

  React.useEffect(() => {
    fetchRooms().then(setRooms).catch(() => {});
    fetchSites().then((d) => setSites(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // A class that issues AHA eCards (its certificates, not its category) offers the AHA modules of its product.
  React.useEffect(() => {
    if (!courseId) { setAha(null); return undefined; }
    let active = true;
    fetchClassCertificateTypes(courseId)
      .then((selection) => {
        if (!(selection?.types || []).includes("AHA")) return null;
        return Promise.all([getAhaEligibility(courseId), getAhaCatalog()]).then(([eligibility, catalog]) => {
          const product = (catalog?.products || []).find((p) => p.key === eligibility?.config?.productKey);
          if (!product) return { modules: [], needsProduct: true };
          return {
            modules: product.modules || [],
            locked: (eligibility.students || []).some((st) => st.issuanceState),
          };
        });
      })
      .then((next) => { if (active) setAha(next); })
      .catch(() => { if (active) setAha(null); }); // no AHA setup: the form simply has no module picker
    return () => { active = false; };
  }, [courseId]);

  // The class's primary location isn't stored; infer it from its sessions: all virtual = a 100% virtual class,
  // otherwise the site of its earliest in-person session.
  const primaryKey = React.useMemo(() => {
    if (sessions.length === 0) return "";
    const keys = [...sessions]
      .sort((a, b) => String(a.sessionDate || a.date || "").localeCompare(String(b.sessionDate || b.date || "")))
      .map((s) => keyForSession(s, sites));
    if (keys.every((k) => k === VIRTUAL)) return VIRTUAL;
    return keys.find((k) => k && k !== VIRTUAL) || "";
  }, [sessions, sites]);

  const roomsById = React.useMemo(
    () => Object.fromEntries(rooms.map((r) => [r.id, r])),
    [rooms],
  );

  const instructorMap = React.useMemo(
    () => Object.fromEntries((courseInstructors || []).map((u) => [u.id, u])),
    [courseInstructors],
  );

  const handleChangeRoom = React.useCallback(async (session, roomId) => {
    const snapshot = session;
    setSessions((prev) => prev.map((s) =>
      s.id === session.id ? { ...s, roomId: roomId ?? null } : s
    ));
    try {
      const updated = await updateSession(session.id, { roomId: roomId ?? null });
      setSessions((prev) => prev.map((s) => s.id === updated.id ? updated : s));
    } catch (err) {
      setSessions((prev) => prev.map((s) => s.id === snapshot.id ? snapshot : s));
      const msg = err?.response?.data?.error || err?.message || "Unknown error";
      setInstructorError(`Failed to update room: ${msg}`);
      console.error("handleChangeRoom failed:", err);
    }
  }, [setSessions]);

  const upcoming = sessions.filter((s) => getStatus(s) !== "completed");
  const past = sessions.filter((s) => getStatus(s) === "completed");
  const capacity = course?.availableSeats || course?.maxCapacity || enrolledCount || 0;
  const nextNum = sessions.length + 1;

  const handleSave = async (formWithLoc) => {
    const { locKey, ...form } = formWithLoc;
    const where = sessionLocationFields(locKey, sites);
    setSaving(true);
    try {
      if (editTarget) {
        const changes = { ...form, ...where, sessionDate: form.date };
        // The server refuses a standard class's session content even when unchanged: send only what a center may change.
        if (contentLocked) STANDARD_SESSION_CONTENT.forEach((key) => delete changes[key]);
        const updated = await updateSession(editTarget.id, changes);
        setSessions((prev) => prev.map((s) => s.id === updated.id ? updated : s));
        setEditTarget(null);
      } else {
        const created = await createSession(courseId, {
          ...form,
          ...where,
          sessionDate: form.date,
          sessionNumber: nextNum,
        });
        setSessions((prev) => [...prev, created]);
        setShowAdd(false);
      }
      if (refreshCourse) refreshCourse();
    } catch (err) {
      const data = err?.response?.data;
      setInstructorError(data?.message || data?.error || "Unable to save the session.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (sessionId) => {
    await deleteSession(sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    setDeleteConfirm(null);
    if (refreshCourse) refreshCourse();
  };

  const handleViewAttendance = (session) => {
    navigate(`/course-management/${courseId}/attendance/${session.id}`);
  };

  const CalIcon = (
    <SvgIcon>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </SvgIcon>
  );

  const CheckIcon = (
    <SvgIcon>
      <polyline points="9 11 12 14 22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </SvgIcon>
  );

  const PlusBtn = canEdit && !contentLocked ? (
    <Box component="button" onClick={() => { setShowAdd((v) => !v); setEditTarget(null); }} sx={{
      height: 34, px: "14px", borderRadius: "10px",
      bgcolor: showAdd ? D.surface3 : D.primary,
      color: showAdd ? D.ink : "var(--color-on-primary)",
      border: showAdd ? `1.5px solid ${D.line}` : 0,
      fontFamily: "inherit", fontSize: "13px", fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: "7px",
      boxShadow: showAdd ? "none" : `0 4px 12px -4px ${alpha(D.primary, 0.45)}`,
      transition: "all .13s",
    }}>
      <SvgIcon size={13} sw={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></SvgIcon>
      New Session
    </Box>
  ) : null;

  return (
    <Box>
      {/* ── Upcoming ──────────────────────────────────────────────────────── */}
      <PanelCard
        icon={CalIcon}
        title="Upcoming Sessions"
        count={upcoming.length}
        headerRight={PlusBtn}
      >
        {/* Add form */}
        {showAdd && !editTarget && (
          <SessionForm
                  contentLocked={contentLocked}
            title="Schedule New Session"
            aha={aha}
            onSave={handleSave}
            onCancel={() => setShowAdd(false)}
            saving={saving}
            sites={sites} primaryKey={primaryKey} capacity={capacity}
          />
        )}

        {/* Upcoming rows */}
        {upcoming.length === 0 && !showAdd && (
          <Box sx={{ px: "20px", py: "24px", textAlign: "center", color: D.ink4, fontSize: "13.5px" }}>
            No upcoming sessions.{canEdit && !contentLocked && " Click 'New Session' to schedule one."}
          </Box>
        )}
        {upcoming.map((s, i) => (
          <React.Fragment key={s.id}>
            {editTarget?.id === s.id && (
              <SessionForm
                  contentLocked={contentLocked}
                title="Edit Session"
                initial={{
                  id: s.id,
                  name: s.name || "",
                  date: s.sessionDate || s.date || "",
                  startTime: s.startTime || "09:00",
                  endTime: s.endTime || "17:00",
                  locKey: keyForSession(s, sites) || (s.location ? legacyKey(s.location) : ""),
                  roomId: s.roomId ?? null,
                  topics: s.topics || "",
                  ahaModuleKeys: s.ahaModuleKeys || [],
                }}
                aha={aha}
                sites={sites} primaryKey={primaryKey} capacity={capacity}
                onSave={handleSave}
                onCancel={() => setEditTarget(null)}
                saving={saving}
              />
            )}
            {deleteConfirm === s.id && (
              <Box sx={{ mx: "20px", my: "8px", p: "14px 16px", bgcolor: D.danger50,
                border: `1.5px solid ${D.danger100}`, borderRadius: "12px",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: D.danger }}>
                  Delete this session? This cannot be undone.
                </Box>
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Box component="button" onClick={() => setDeleteConfirm(null)} sx={{
                    height: 32, px: "12px", borderRadius: "8px", border: `1.5px solid ${D.line}`,
                    bgcolor: "var(--color-surface-canvas-light)", fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700,
                    color: D.ink2, cursor: "pointer",
                  }}>Cancel</Box>
                  <Box component="button" onClick={() => handleDelete(s.id)} sx={{
                    height: 32, px: "12px", borderRadius: "8px", border: 0,
                    bgcolor: D.danger, color: "var(--color-on-primary)",
                    fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700, cursor: "pointer",
                  }}>Delete</Box>
                </Box>
              </Box>
            )}
            {editTarget?.id !== s.id && (
              <SessionRow
                contentLocked={contentLocked}
                session={s}
                capacity={capacity}
                canEdit={canEdit}
                isLast={i === upcoming.length - 1}
                onEdit={(s) => { setEditTarget(s); setShowAdd(false); }}
                onDelete={(id) => setDeleteConfirm(id)}
                onViewAttendance={handleViewAttendance}
                instructorMap={instructorMap}
                room={roomsById[s.roomId]}
                rooms={rooms}
                onChangeRoom={handleChangeRoom}
              />
            )}
          </React.Fragment>
        ))}
      </PanelCard>

      {/* ── Past ──────────────────────────────────────────────────────────── */}
      {past.length > 0 && (
        <PanelCard
          iconBg={D.green50} iconColor={D.green}
          icon={CheckIcon}
          title="Past Sessions"
          count={past.length}
        >
          {past.map((s, i) => (
            <React.Fragment key={s.id}>
              {editTarget?.id === s.id && (
                <SessionForm
                  contentLocked={contentLocked}
                  title="Edit Session"
                  initial={{
                    id: s.id,
                    name: s.name || "",
                    date: s.sessionDate || s.date || "",
                    startTime: s.startTime || "09:00",
                    endTime: s.endTime || "17:00",
                    locKey: keyForSession(s, sites) || (s.location ? legacyKey(s.location) : ""),
                    roomId: s.roomId ?? null,
                    topics: s.topics || "",
                    ahaModuleKeys: s.ahaModuleKeys || [],
                  }}
                  aha={aha}
                  sites={sites} primaryKey={primaryKey} capacity={capacity}
                  onSave={handleSave}
                  onCancel={() => setEditTarget(null)}
                  saving={saving}
                />
              )}
              {editTarget?.id !== s.id && (
                <SessionRow
                contentLocked={contentLocked}
                  session={s}
                  capacity={capacity}
                  canEdit={canEdit}
                  isLast={i === past.length - 1}
                  onEdit={(s) => { setEditTarget(s); setShowAdd(false); }}
                  onDelete={(id) => setDeleteConfirm(id)}
                  onViewAttendance={handleViewAttendance}
                  instructorMap={instructorMap}
                  room={roomsById[s.roomId]}
                  rooms={rooms}
                  onChangeRoom={handleChangeRoom}
                />
              )}
            </React.Fragment>
          ))}
        </PanelCard>
      )}

      <Snackbar
        open={Boolean(instructorError)}
        autoHideDuration={6000}
        onClose={() => setInstructorError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setInstructorError(null)} sx={{ borderRadius: "10px" }}>
          {instructorError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
